<template>
  <ContextColorProvider context="trade">
    <Notification group="wrn-trade" />
    <TwoColumnsContainerLoader v-if="loading" />
    <div v-else-if="error" class="trade-view__error-container">
      <ActionableHelperView
        class="trade-view__error-container__error"
        :on-retry="load"
      />
    </div>
    <div v-else-if="errorHomeBroker" class="trade-view__error-container">
      <ActionableHelperView
        class="trade-view__error-container__error"
        :on-retry="onBrokerError"
        :type="errorType"
        :button-text="errorButton"
      />
    </div>
    <template v-else>
      <TwoColumnsContainer has-padding single-scroll>
        <template #main>
          <TradeMain
            :dash-status="dashStatusCard"
            :account-buttons="accountButtons"
            :balances="balances"
            @track-trade-dash-buttons-event="handleDashButtonsEvents"
          />
        </template>
        <template #content>
          <TradeSidebar
            :balances="balances"
            :trade-status="tradeStatus"
            :on-access-home-broker-click="fetchHomeBroker"
            :on-access-copytrade-click="fetchCopytrade"
          />
        </template>
      </TwoColumnsContainer>
    </template>
  </ContextColorProvider>
</template>

<script lang="ts">
import { Component, Vue } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import { TradeSidebar } from './components/TradeSidebar';
import { TradeMain } from './components/TradeMain';
import {
  ActionableHelperView,
  ContextColorProvider
} from '@warrenbrasil/nebraska-web';

import {
  TwoColumnsContainer,
  TwoColumnsContainerLoader
} from '@/modules/common/components/TwoColumnsContainer';
import {
  postHomeBrokerToken,
  getHomeBrokerUrl
} from '@/modules/trade/services/home-broker';
import {
  postCopytradeToken,
  COPYTRADE_PLATFORMS,
  CopytradePlatform
} from '@/modules/trade/services/copytrade';
import { FeatureFlagsModule } from '@/modules/common/store/FeatureFlagsModule';
import { FeatureFlags } from '@/types/models/FeatureFlags';
import { Notification } from '@/modules/common/components/Notification';
import { notify } from '@/modules/common/helpers/notification';
import { NotificationTypes } from '@/modules/common/services/http/notification';
import {
  IDashButtons,
  IHeroBalances,
  AccountStatus,
  IDashStatusCard
} from '@/modules/trade/services/account';
import { ErrorMessages, BrokerType, BrokerButtonLabel } from './types';

import {
  trackTradeViewLoad,
  trackHomeBrokerClick,
  trackDashStatusButtonClick
} from './trackers';

import { WarrenModules } from '@/types/navigation';
const tradePositionsModule = namespace('tradeModule');
const customerModule = namespace('CustomerModule');

@Component({
  components: {
    ContextColorProvider,
    TradeSidebar,
    TradeMain,
    TwoColumnsContainer,
    TwoColumnsContainerLoader,
    ActionableHelperView,
    Notification
  }
})
export default class TradeView extends Vue {
  private loading = true;
  private error = false;
  private errorHomeBroker = false;
  private errorType = 'generic';
  private errorButton = 'Tentar novamente';
  private homeBrokeraction = '';
  // TODO: remove hardcode when BFF exposes copytrade URL endpoint
  private copytradeUrl = String(process.env.VUE_APP_COPYTRADE_URL || '');

  @tradePositionsModule.Action('resetTradeHomeStates')
  private resetTradeHomeStates!: Function;

  @tradePositionsModule.Action('fetchBalances')
  private fetchBalances!: Function;

  @tradePositionsModule.Action('fetchDashStatus')
  private fetchDashStatus!: Function;

  @tradePositionsModule.State('balances')
  readonly balances!: IHeroBalances;

  @tradePositionsModule.State('dashStatusCard')
  readonly dashStatusCard!: IDashStatusCard | null;

  @tradePositionsModule.State('accountButtons')
  readonly accountButtons!: IDashButtons[];

  @customerModule.Getter('getCustomerId') private customer!: string;

  @tradePositionsModule.State('tradeStatus')
  readonly tradeStatus!: AccountStatus;

  @tradePositionsModule.Action('fetchStatus')
  private fetchStatus!: Function;

  public readonly WarrenModules = WarrenModules;

  mounted() {
    this.load();
  }

  destroyed() {
    this.resetTradeHomeStates();
  }

  private async load() {
    this.loading = true;
    this.error = false;
    await Promise.all([
      this.fetchDashStatus(),
      this.fetchStatus(),
      this.fetchBalances(),
      this.fetchHomeBrokerUrl()
    ]);
    trackTradeViewLoad();
    this.loading = false;
  }

  private async fetchHomeBrokerUrl() {
    try {
      const url = await getHomeBrokerUrl();
      this.homeBrokeraction = url;
    } catch (_) {
      this.homeBrokeraction = String(
        process.env.VUE_APP_HOME_BROKER_NELOGICA_URL
      );
    }
  }

  private createHomeBrokerForm() {
    const action = this.homeBrokeraction;
    const form = document.createElement('form');
    const input = document.createElement('input');

    form.appendChild(input);

    form.action = action;
    form.method = 'POST';

    input.type = 'hidden';
    input.name = 'token';

    return { form, input };
  }

  /**
   * Creates a form that holds the home broker integration.
   *
   * Returns a tuple, containing { form, input }:
   *
   * - form: HTMLFormElement, used to submit data
   * - input: HTMLInputElement that contains the token used on the login integration
   */

  private fetchHomeBroker() {
    trackHomeBrokerClick('Acessar Home Broker');
    postHomeBrokerToken(this.customer)
      // eslint-disable-next-line promise/always-return
      .then(({ token }) => {
        const { form, input } = this.createHomeBrokerForm();
        const homeBrokerWindow = globalThis.open() as Window;
        const target = homeBrokerWindow.document.body;
        input.value = token;

        target.appendChild(form);
        form.submit();
      })
      .catch(error => {
        const errorMessage = error.message;
        this.errorHomeBroker = true;
        if (
          errorMessage === ErrorMessages.invalidId ||
          errorMessage === ErrorMessages.invalidRequest
        ) {
          this.errorType = BrokerType.instability;
          this.errorButton = BrokerButtonLabel.returnToHome;
        }
      });
  }

  private onBrokerError() {
    if (this.errorType === BrokerType.generic) {
      this.fetchHomeBroker();
    } else {
      this.errorHomeBroker = false;
    }
  }

  private createCopytradeForm() {
    const action = this.copytradeUrl;
    const form = document.createElement('form');
    const input = document.createElement('input');

    form.appendChild(input);

    form.action = action;
    form.method = 'POST';

    input.type = 'hidden';
    input.name = 'token';

    return { form, input };
  }

  private fetchCopytrade() {
    if (FeatureFlagsModule.isEnabled(FeatureFlags.DISABLE_ELLIOT)) return;

    trackHomeBrokerClick('Universo Elliot');
    const universoElliot =
      COPYTRADE_PLATFORMS[CopytradePlatform.UniversoElliot];
    postCopytradeToken({ portfolioTypeId: universoElliot.portfolioTypeId })
      .then(({ token }) => {
        const { form, input } = this.createCopytradeForm();
        const copytradeWindow = globalThis.open() as Window;
        const target = copytradeWindow.document.body;
        input.value = token;

        target.appendChild(form);
        form.submit();
      })
      .catch(error => {
        const errorMessage = error.message;
        this.errorHomeBroker = true;
        if (
          errorMessage === ErrorMessages.invalidId ||
          errorMessage === ErrorMessages.invalidRequest
        ) {
          this.errorType = BrokerType.instability;
          this.errorButton = BrokerButtonLabel.returnToHome;
        }
      });
  }

  private handleDashButtonsEvents(dashStatusAction?: string | null) {
    return trackDashStatusButtonClick(dashStatusAction);
  }
}
</script>

<style lang="less" scoped>
@navbar-height: 64px;
.trade-view {
  &__error-container {
    display: flex;
    align-items: center;
    height: 100vh;
  }

  &__error-container__error {
    width: 100%;
  }

  &__warning {
    position: relative;
    margin-bottom: -@navbar-height;

    @media @tabletDown {
      @navbar-height: 56px;
      margin-bottom: -@navbar-height;
    }
  }
}
</style>
